import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type {
  DataGridPremiumProps,
  GridDataSource,
  GridGetRowsError,
  GridGetRowsParams,
  GridGetRowsResponse,
  GridPaginationModel,
  GridUpdateRowError,
} from "@mui/x-data-grid-premium";

export type CursorPageResponse<R> = {
  rows: R[];
  pageInfo: {
    hasNextPage: boolean;
    nextCursor?: string;
  };
};

export type CursorFetchArgs = {
  cursor?: string;
  limit: number;
};

export type CursorFetchPage<R> = (
  args: CursorFetchArgs
) => Promise<CursorPageResponse<R>>;

const DEFAULT_PAGE_SIZE = 100;

type UseCursorDataSourceOptions = {
  initialPageSize?: number;
};

type CursorDataGridProps = Pick<
  DataGridPremiumProps,
  | "dataSource"
  | "onDataSourceError"
  | "pagination"
  | "loading"
  | "rowCount"
  | "paginationModel"
  | "onPaginationModelChange"
  | "pageSizeOptions"
  | "initialState"
>;

type UseCursorDataSourceReturn<R> = {
  /**
   * Clears cursor state, resets to page 0, and replaces dataSource so MUI
   * refetches. Use after mutations. Filter/search changes are handled
   * automatically when those values are included in fetchPage's useCallback deps.
   */
  reset: () => void;
  fetchedRows: R[];
  dataGridProps: CursorDataGridProps;
};

export const useCursorDataSource = <R>(
  fetchPage: CursorFetchPage<R>,
  options: UseCursorDataSourceOptions = {}
): UseCursorDataSourceReturn<R> => {
  const { initialPageSize = DEFAULT_PAGE_SIZE } = options;

  const [isLoading, setIsLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: initialPageSize,
  });
  const knownTotalRef = useRef<number | undefined>(undefined);
  const [totalRowCount, setTotalRowCount] = useState<number | undefined>(
    undefined
  );
  const pageToNextCursorRef = useRef<Record<number, string | undefined>>({});
  const [fetchedRows, setFetchedRows] = useState<R[]>([]);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);
      if (!(model.page in pageToNextCursorRef.current)) {
        setIsLoading(true);
      }
    },
    []
  );

  const onDataSourceError = useCallback(
    (error: GridGetRowsError<GridGetRowsParams> | GridUpdateRowError) => {
      console.error(error);
    },
    []
  );

  const createDataSource = useCallback((): GridDataSource => {
    return {
      getRows: async (
        params: GridGetRowsParams
      ): Promise<GridGetRowsResponse> => {
        const { page, pageSize } = params.paginationModel ?? {
          page: 0,
          pageSize: initialPageSize,
        };

        const cursor = pageToNextCursorRef.current[page - 1];

        if (page > 0 && cursor === undefined) {
          setIsLoading(false);
          return {
            rows: [],
            rowCount: knownTotalRef.current ?? -1,
            pageInfo: { hasNextPage: false },
          };
        }

        if (!(page in pageToNextCursorRef.current)) {
          setIsLoading(true);
        }

        try {
          const resp = await fetchPage({
            cursor: cursor as string | undefined,
            limit: pageSize,
          });

          if (!(page in pageToNextCursorRef.current)) {
            setFetchedRows((prev) => [...prev, ...resp.rows]);
          }

          // eslint-disable-next-line require-atomic-updates
          pageToNextCursorRef.current[page] = resp.pageInfo.nextCursor;

          if (!resp.pageInfo.hasNextPage) {
            const total = page * pageSize + resp.rows.length;
            knownTotalRef.current = total;
            setTotalRowCount(total);
          }

          const rowCount =
            knownTotalRef.current ??
            (resp.pageInfo.hasNextPage
              ? -1
              : page * pageSize + resp.rows.length);

          return {
            rows: resp.rows as GridGetRowsResponse["rows"],
            rowCount,
            pageInfo: resp.pageInfo,
          };
        } finally {
          setIsLoading(false);
        }
      },
    };
  }, [fetchPage, initialPageSize]);

  const [dataSource, setDataSource] = useState(() => createDataSource());

  const reset = useCallback(() => {
    pageToNextCursorRef.current = {};
    knownTotalRef.current = undefined;
    setTotalRowCount(undefined);
    setFetchedRows([]);
    setIsLoading(true);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    // New object identity makes MUI clear its cache and refetch.
    setDataSource(createDataSource());
  }, [createDataSource]);

  // When fetchPage changes (filters/search), reset so MUI refetches with the new
  // query. Skip the first run — initial state already set up the dataSource.
  const isFirstDataSourceSync = useRef(true);
  useLayoutEffect(() => {
    if (isFirstDataSourceSync.current) {
      isFirstDataSourceSync.current = false;
      return;
    }
    reset();
  }, [reset]);

  const dataGridProps = useMemo(
    (): CursorDataGridProps => ({
      dataSource,
      onDataSourceError,
      pagination: true, // needs to be set for cursor based
      loading: isLoading, // to smooth out loading states
      rowCount: totalRowCount ?? -1, // override so that when we have the final count, it is utilized instead of the 'more than x'
      pageSizeOptions: [initialPageSize], // necessary as a workaround to a MUI bug (https://github.com/mui/mui-x/issues/22983) we found that borks if you are doing datasource and try to change page size
      paginationModel, // necessary since our search is not integrated into our grid (if we used the built in filterModel, this wouldn't be necessary)
      onPaginationModelChange: handlePaginationModelChange, // same 'filterModel' comment
      initialState: {
        pagination: {
          rowCount: -1,
        },
      },
    }),
    [
      dataSource,
      onDataSourceError,
      isLoading,
      totalRowCount,
      initialPageSize,
      paginationModel,
      handlePaginationModelChange,
    ]
  );

  return {
    reset,
    fetchedRows,
    dataGridProps,
  };
};
